import { useState, useEffect, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { usePhoneNumberInput } from '../../hooks/usePhoneNumberInput';
import { getAuth } from 'firebase/auth';
import { useFirebaseFunction } from '@/hooks/useFirebaseFunction';
import { callFirebaseFunction } from '@/bridge/callFirebaseFunction';

// Define the types for our API calls
type UpdateUserPrivateInfoRequest = {
  updates: Partial<{
    firstName?: string;
    lastName?: string;
    // other fields from UserPrivateInfo
  }>;
};

type UpdateUserPrivateInfoResponse = {
    success: boolean;
};

type GetUserPrivateInfoRequest = {
  fields?: string[];
};

type GetUserPrivateInfoResponse = {
  userInfo: {
    firstName?: string;
    lastName?: string;
    // other fields that might be returned
  };
};



export function AccountSettings() {
    // State for form fields
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [initialFirstName, setInitialFirstName] = useState('');
    const [initialLastName, setInitialLastName] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    
    const initialPhoneNumber = '';
    const { phoneNumber, phoneError, handlePhoneChange } = usePhoneNumberInput(initialPhoneNumber);
    
    // Use the hook to fetch user data
    const [userData, isLoading, fetchError,] = useFirebaseFunction<
      GetUserPrivateInfoRequest, 
      GetUserPrivateInfoResponse
    >(
      'getUserPrivateInfo',
      (fn) => fn({ fields: ["firstName", "lastName"] }),
      []
    );

    const auth = getAuth();

    // Set form values when user data is loaded
    useEffect(() => {
        if (userData && !isLoading) {
            const fetchedFirstName = userData.userInfo.firstName || '';
            const fetchedLastName = userData.userInfo.lastName || '';
            
            setFirstName(fetchedFirstName);
            setLastName(fetchedLastName);
            setInitialFirstName(fetchedFirstName);
            setInitialLastName(fetchedLastName);
        }
    }, [userData, isLoading]);

    // Show error toast if fetch fails
    useEffect(() => {
        if (fetchError) {
            // toast({
            //     title: "Error",
            //     description: "Failed to load account information. Please try again.",
            //     variant: "destructive"
            // });
        }
    }, [fetchError]);

    // Memoize the hasChanges value
    const hasChanges = useMemo(() => {
        return firstName !== initialFirstName ||
               lastName !== initialLastName;
    }, [firstName, lastName, initialFirstName, initialLastName]);

    // Check if form is valid (for enabling save button)
    const isFormValid = useMemo(() => {
        return hasChanges && phoneError === '';
    }, [hasChanges, phoneError]);

    // Handle save button click
    const handleSave = async () => {
        if (!isFormValid) return;
        
        try {
            setIsSaving(true);
            
            // Only include fields that have changed
            // biome-ignore lint/suspicious/noExplicitAny: <explanation>
            const updates: Record<string, any> = {};
            
            if (firstName !== initialFirstName) {
                updates.firstName = firstName;
            }
            
            if (lastName !== initialLastName) {
                updates.lastName = lastName;
            }
            
            // Call the update function directly
            const response = await callFirebaseFunction<
              UpdateUserPrivateInfoRequest,
              UpdateUserPrivateInfoResponse
            >(
              "updateUserPrivateInfo",
              { updates }
            );
            
            if (response.success) {
                // Update initial values to match current values
                setInitialFirstName(firstName);
                setInitialLastName(lastName);
                
                // toast({
                //     title: "Success",
                //     description: "Your account information has been updated.",
                // });
            }
        } catch (error) {
            console.error("Error saving user data:", error);
            // toast({
            //     title: "Error",
            //     description: "Failed to save changes. Please try again.",
            //     variant: "destructive"
            // });
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div className="mb-8">
            <h1 className="text-2xl font-bold text-blue-dark mb-1">Account settings</h1>
            <p className="text-blue-dark mb-6 text-sm">
                View and make changes to your account.
                Click save when you're done.
            </p>
            
            {isLoading ? (
                <div className="flex justify-center py-8">
                    <div className="animate-spin h-8 w-8 border-4 border-blue border-t-transparent rounded-full" />
                </div>
            ) : (
                <>
                    <div className="mb-4">
                        <label className="block text-blue-dark mb-2">Full Legal Name</label>
                        <div className="flex gap-4 w-full">
                            <input
                                type="text"
                                value={firstName}
                                onChange={(e) => setFirstName(e.target.value)}
                                className="w-1/2 border border-slate-300 rounded px-3 py-2"
                                placeholder="First name"
                            />
                            <input
                                type="text"
                                value={lastName}
                                onChange={(e) => setLastName(e.target.value)}
                                className="w-1/2 border border-slate-300 rounded px-3 py-2"
                                placeholder="Last name"
                            />
                        </div>
                    </div>
                    
                    <div className="mb-4">
                        <label className="block text-blue-dark mb-2">Phone Number (Locked)</label>
                        <input
                            type="tel"
                            value={auth?.currentUser?.phoneNumber || phoneNumber}
                            onChange={handlePhoneChange}
                            disabled={true}
                            className={`w-full border ${phoneError ? 'border-red-500' : 'border-slate-300'} rounded px-3 py-2`}
                            placeholder="+1 (555) 123-4567"
                        />
                        {phoneError && (
                            <p className="text-red-500 text-sm mt-1">{phoneError}</p>
                        )}
                        <p className="text-gray-500 text-xs mt-1">US numbers only. Contact support to change</p>
                    </div>
                    
                    <Button 
                        className={`py-6 bg-blue text-white text-2xl font-bold w-full rounded-lg ${!isFormValid ? 'opacity-50' : ''}`}
                        disabled={!isFormValid || isSaving}
                        onClick={handleSave}
                    >
                        {isSaving ? 'Saving...' : 'Save Changes'}
                    </Button>
                </>
            )}
        </div>
    );
}